// src/components/Offcanvas.jsx
import React, { useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { ReactSVG } from "react-svg";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { tokenName, iName } from "../../constant"; // ✅ correct path from /components

const withBase = (p) =>
  `${import.meta.env.BASE_URL}${String(p || "").replace(/^\//, "")}`;

const Offcanvas = ({ show, activeStatus }) => {
  const navRef = useRef(null);
  const navigate = useNavigate();

  // ✅ cookie options to remove correctly (path must match how you set it)
  const cookieOptions = useMemo(() => {
    const isHttps =
      typeof window !== "undefined" && window.location?.protocol === "https:";
    return {
      path: "/",
      sameSite: "lax",
      secure: isHttps,
    };
  }, []);

  // Close on any <li> click (event delegation)
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const handleClick = (e) => {
      const li = e.target.closest("li");
      if (li) activeStatus(false);
    };

    nav.addEventListener("click", handleClick);
    return () => nav.removeEventListener("click", handleClick);
  }, [activeStatus]);

  const handleLogout = (e) => {
    e.preventDefault();

    try {
      // ✅ remove cookie-based auth
      Cookies.remove(tokenName, cookieOptions);
      Cookies.remove(iName, cookieOptions);

      // ✅ backward cleanup: if older cookies were set without options/path
      Cookies.remove(tokenName);
      Cookies.remove(iName);

      // ✅ clear axios header used by your backend
      if (axios.defaults?.headers?.common?.["x-id-token"]) {
        delete axios.defaults.headers.common["x-id-token"];
      }
    } catch {
      // ignore
    }

    activeStatus(false);
    navigate("/sign-in", { replace: true });
  };

  // ✅ name from cookie
  const name = (() => {
    try {
      return Cookies.get(iName) || "User";
    } catch {
      return "User";
    }
  })();

  return (
    <div
      className={`offcanvas-menu ${show ? "active" : ""}`}
      role="dialog"
      aria-hidden={!show}
    >
      <div className="profile-card text-center">
        <div className="profile-card__content">
          <p className="name">{name}</p>
        </div>
      </div>

      <div className="offcanvas-navigation-wrapper space-mt--40">
        <ul className="offcanvas-navigation" ref={navRef}>
          {/* Profile */}
          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/profile-two.svg")} />
            </span>
            <Link to="/profile">My Profile</Link>
          </li>

          {/* Settings */}
          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/gear-two.svg")} />
            </span>
            <Link to="/edit-profile">Settings</Link>
          </li>

          {/* ---- Explore & Track ---- */}
          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/product.svg")} />
            </span>
            <Link to="/l/project">Projects (Ideation)</Link>
          </li>

          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/cart-two.svg")} />
            </span>
            <Link to="/f/project">Projects (Funding)</Link>
          </li>

          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/cart-three.svg")} />
            </span>
            <Link to="/transaction">Transactions</Link>
          </li>

          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/wishlist.svg")} />
            </span>
            <Link to="/ideation">Interested (Ideation)</Link>
          </li>

          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/cart.svg")} />
            </span>
            <Link to="/funding">Invested (Funding)</Link>
          </li>

          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/notification.svg")} />
            </span>
            <Link to="/log">Logs</Link>
          </li>

          {/* Logout */}
          <li>
            <span className="icon">
              <ReactSVG src={withBase("assets/img/icons/security.svg")} />
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="btn btn-link p-0 text-start"
              style={{ textDecoration: "none" }}
            >
              Logout
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

Offcanvas.propTypes = {
  show: PropTypes.bool,
  activeStatus: PropTypes.func.isRequired,
};

export default Offcanvas;
